import { PUBLICATION_MODES } from './publicationMode.js';
import {
  PUBLIC_AUTH_CALLBACK_PATH,
  STAFF_LOGIN_PATH,
} from './publicationAccess.js';
import { isSelectiveHiddenSupportFaqPath } from './supportFaqVisibility.js';

export const PUBLICATION_ROUTE_CLASSES = Object.freeze({
  PUBLIC: 'public',
  AUTH_PUBLIC: 'auth_public',
  AUTHENTICATED_MINIMAL: 'authenticated_minimal',
  HIDDEN_PRODUCT: 'hidden_product',
  INTERNAL_ADMIN: 'internal_admin',
  STAFF_ACCESS: 'staff_access',
  OPERATIONAL: 'operational',
  PROVIDER_RUNTIME: 'provider_runtime',
  TOKEN_UTILITY: 'token_utility',
  LEGACY: 'legacy',
  DEV_TEST: 'dev_test',
  UNKNOWN: 'unknown',
});

export const PUBLICATION_ROUTE_OUTCOMES = Object.freeze({
  CONTENT: 'content',
  LEVEL_LOCKED: 'level_locked',
  GAME_OVER: 'game_over',
});

const normalizePathname = (value) => {
  if (typeof value !== 'string') {
    return '/';
  }

  const pathname = value.split(/[?#]/u, 1)[0].trim();
  if (!pathname) {
    return '/';
  }

  const withLeadingSlash = pathname.startsWith('/') ? pathname : `/${pathname}`;
  const collapsed = withLeadingSlash.replace(/\/{2,}/gu, '/');
  return collapsed === '/' ? collapsed : collapsed.replace(/\/+$/u, '');
};

const normalizeConfiguredPath = (value, fallback) => {
  const pathname = normalizePathname(value);
  return pathname === '/' ? fallback : pathname;
};

const splitSegments = (pathname) =>
  normalizePathname(pathname).split('/').filter(Boolean);

const matchesExact = (pathname, candidates) => candidates.includes(pathname);

const matchesSingleSegmentRoute = (pathname, prefix) => {
  const pathSegments = splitSegments(pathname);
  const prefixSegments = splitSegments(prefix);

  return (
    pathSegments.length === prefixSegments.length + 1 &&
    prefixSegments.every((segment, index) => pathSegments[index] === segment)
  );
};

const matchesOneOrTwoSegmentRoute = (pathname, prefix) => {
  const pathSegments = splitSegments(pathname);
  const prefixSegments = splitSegments(prefix);
  const suffixLength = pathSegments.length - prefixSegments.length;

  return (
    (suffixLength === 1 || suffixLength === 2) &&
    prefixSegments.every((segment, index) => pathSegments[index] === segment)
  );
};

const matchesPrefix = (pathname, prefix) =>
  pathname === prefix || pathname.startsWith(`${prefix}/`);

const result = (pathname, routeClass, routeId) => ({
  pathname,
  routeClass,
  routeId,
});

export const classifyPublicationRoute = (value, options = {}) => {
  const pathname = normalizePathname(value);
  const staffLoginPath = normalizeConfiguredPath(
    options.staffLoginPath,
    STAFF_LOGIN_PATH,
  );
  const staffResetPath = normalizeConfiguredPath(
    options.staffResetPath,
    `${staffLoginPath}/reset-password`,
  );

  if (matchesExact(pathname, [staffLoginPath, staffResetPath])) {
    return result(pathname, PUBLICATION_ROUTE_CLASSES.STAFF_ACCESS, 'staff-access');
  }

  if (pathname === '/maintenance') {
    return result(pathname, PUBLICATION_ROUTE_CLASSES.OPERATIONAL, 'maintenance');
  }

  if (
    matchesExact(pathname, [
      '/ext/twitch/config',
      '/ext/twitch/config.html',
      '/ext/twitch/panel',
      '/ext/twitch/panel.html',
      '/ext/twitch/overlay',
      '/ext/twitch/overlay.html',
      '/ext/twitch/video_overlay.html',
    ])
  ) {
    return result(pathname, PUBLICATION_ROUTE_CLASSES.PROVIDER_RUNTIME, 'twitch-extension');
  }

  if (matchesSingleSegmentRoute(pathname, '/newsletter/unsubscribe')) {
    return result(pathname, PUBLICATION_ROUTE_CLASSES.TOKEN_UTILITY, 'newsletter-unsubscribe');
  }

  if (matchesSingleSegmentRoute(pathname, '/support/feedback')) {
    return result(pathname, PUBLICATION_ROUTE_CLASSES.TOKEN_UTILITY, 'support-feedback');
  }

  if (matchesPrefix(pathname, '/__preview__') || matchesPrefix(pathname, '/__e2e__')) {
    return result(pathname, PUBLICATION_ROUTE_CLASSES.DEV_TEST, 'dev-test');
  }

  if (
    matchesExact(pathname, ['/gamedetails', '/articledetails']) ||
    matchesSingleSegmentRoute(pathname, '/games/legacy')
  ) {
    return result(pathname, PUBLICATION_ROUTE_CLASSES.LEGACY, 'legacy');
  }

  if (matchesPrefix(pathname, '/gamedatamanager')) {
    return result(pathname, PUBLICATION_ROUTE_CLASSES.INTERNAL_ADMIN, 'game-data-manager');
  }

  if (matchesSingleSegmentRoute(pathname, '/deal-finder/outbound')) {
    return result(
      pathname,
      PUBLICATION_ROUTE_CLASSES.PUBLIC,
      'deal-finder-outbound',
    );
  }

  if (pathname === '/account/privacy/delete-confirmation') {
    return result(pathname, PUBLICATION_ROUTE_CLASSES.AUTH_PUBLIC, 'delete-confirmation');
  }

  if (pathname === '/account/privacy') {
    return result(pathname, PUBLICATION_ROUTE_CLASSES.AUTHENTICATED_MINIMAL, 'account-privacy');
  }

  if (
    matchesExact(pathname, [
      '/dashboard',
      '/library',
      '/live',
      '/pricing',
      '/checkout',
      '/payment-methods/new',
    ]) ||
    matchesSingleSegmentRoute(pathname, '/u')
  ) {
    return result(pathname, PUBLICATION_ROUTE_CLASSES.HIDDEN_PRODUCT, 'hidden-product');
  }

  if (isSelectiveHiddenSupportFaqPath(pathname)) {
    return result(
      pathname,
      PUBLICATION_ROUTE_CLASSES.HIDDEN_PRODUCT,
      'selective-hidden-support-faq',
    );
  }

  if (matchesExact(pathname, ['/login', '/signup', '/reset-password', PUBLIC_AUTH_CALLBACK_PATH])) {
    return result(pathname, PUBLICATION_ROUTE_CLASSES.AUTH_PUBLIC, 'public-auth');
  }

  if (pathname === '/deal-finder') {
    return result(pathname, PUBLICATION_ROUTE_CLASSES.PUBLIC, 'deal-finder');
  }

  if (
    matchesExact(pathname, [
      '/',
      '/games',
      '/news',
      '/news/archive',
      '/support',
      '/support/faq',
      '/support/report-illegal-content',
      '/legal',
    ]) ||
    matchesSingleSegmentRoute(pathname, '/games') ||
    matchesSingleSegmentRoute(pathname, '/news') ||
    matchesOneOrTwoSegmentRoute(pathname, '/support/faq') ||
    matchesSingleSegmentRoute(pathname, '/legal')
  ) {
    return result(pathname, PUBLICATION_ROUTE_CLASSES.PUBLIC, 'public-surface');
  }

  return result(pathname, PUBLICATION_ROUTE_CLASSES.UNKNOWN, 'unknown');
};

const SELECTIVE_ALLOWED_ROUTE_CLASSES = new Set([
  PUBLICATION_ROUTE_CLASSES.PUBLIC,
  PUBLICATION_ROUTE_CLASSES.AUTH_PUBLIC,
  PUBLICATION_ROUTE_CLASSES.AUTHENTICATED_MINIMAL,
  PUBLICATION_ROUTE_CLASSES.STAFF_ACCESS,
  PUBLICATION_ROUTE_CLASSES.OPERATIONAL,
  PUBLICATION_ROUTE_CLASSES.PROVIDER_RUNTIME,
  PUBLICATION_ROUTE_CLASSES.TOKEN_UTILITY,
  PUBLICATION_ROUTE_CLASSES.LEGACY,
]);

export const isSelectivePublicationRouteAllowed = (routeOrClass) => {
  const routeClass =
    typeof routeOrClass === 'string' ? routeOrClass : routeOrClass?.routeClass;
  return SELECTIVE_ALLOWED_ROUTE_CLASSES.has(routeClass);
};

export const resolvePublicationRouteOutcome = ({
  publicationMode,
  routeClass,
  isPrivilegedUser = false,
  isProduction = true,
} = {}) => {
  if (routeClass === PUBLICATION_ROUTE_CLASSES.UNKNOWN) {
    return PUBLICATION_ROUTE_OUTCOMES.GAME_OVER;
  }

  if (routeClass === PUBLICATION_ROUTE_CLASSES.DEV_TEST) {
    return isProduction
      ? PUBLICATION_ROUTE_OUTCOMES.GAME_OVER
      : PUBLICATION_ROUTE_OUTCOMES.CONTENT;
  }

  if (routeClass === PUBLICATION_ROUTE_CLASSES.INTERNAL_ADMIN) {
    return isPrivilegedUser
      ? PUBLICATION_ROUTE_OUTCOMES.CONTENT
      : PUBLICATION_ROUTE_OUTCOMES.GAME_OVER;
  }

  if (routeClass === PUBLICATION_ROUTE_CLASSES.HIDDEN_PRODUCT) {
    if (isPrivilegedUser || publicationMode === PUBLICATION_MODES.FULL) {
      return PUBLICATION_ROUTE_OUTCOMES.CONTENT;
    }

    if (publicationMode === PUBLICATION_MODES.SELECTIVE) {
      return PUBLICATION_ROUTE_OUTCOMES.LEVEL_LOCKED;
    }
  }

  return PUBLICATION_ROUTE_OUTCOMES.CONTENT;
};
